import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { CompanyDetails } from './CompanyDetails';
import { Projects } from './Projects';
import { Applications } from './Applications';
import { Technology } from './Technology';
import { CustomerResponse } from '../types';
import { customerApi, userApi } from '../services/api';
import ArtOfPossible from './ArtOfPossible';
import Transformation from './Transformation';
import Frameworks from './Frameworks';
import GoalsEditTab from './GoalsEditTab';
import CustomerSidebar from '../components/customer/CustomerSidebar';
import CustomerDashboardTab from './dashboard_pages/CustomerDashboardTab';
import { HelpModal } from '../components/common/HelpModal';
import CustomerDemoPresentation from '../components/common/CustomerDemoPresentation';
import { useCustomer } from '../context/CustomerContext';
import type { CustomerSection } from '../context/CustomerContext';
import { DASHBOARD_SECTIONS, DashboardSectionId, getDashboardSectionAnchorId } from './dashboard_pages/dashboardSections';

interface LocationState {
    customerData?: CustomerResponse;
    fromDashboard?: boolean;
}

export const CustomerDetails: React.FC = () => {
    const { customerId } = useParams<{ customerId?: string }>();
    const location = useLocation();
    const { customerData: initialCustomerData, fromDashboard } =
        (location.state as LocationState) || {};

    const { 
        customerData, 
        isLoading, 
        loadingSection,
        dashboardLoading,
        error: contextError,
        setCustomerData, 
        fetchCustomerData,
        fetchDashboard,
        fetchSection,
        isSectionLoaded,
    } = useCustomer();

    const [activeTab, setActiveTab] = useState<string>('company-details');
    const [dashActiveTab, setDashActiveTab] = useState<string>('customer-dashboard');
    const [dashboardEnabled, setDashboardEnabled] = useState(fromDashboard || false);
    const [isUserCustomer, setIsUserCustomer] = useState<boolean>(false);
    const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
    const [pageError, setPageError] = useState<string | null>(null);
    const [shouldOpenNewProject, setShouldOpenNewProject] = useState<boolean>(false);
    const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
    const [showCustomerDemo, setShowCustomerDemo] = useState<boolean>(false);
    const mainContentRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setPageError(null);
                // Clear previous customer data when customerId changes
                if (customerId && customerData?.customer_id !== customerId) {
                    setCustomerData(undefined);
                }

                // If we have initial customer data from location state, set it in context
                if (initialCustomerData && customerId) {
                    setCustomerData(initialCustomerData);
                } else if (customerId) {
                    // Always fetch fresh data when customerId changes
                    await fetchCustomerData(customerId);
                }

                const currentUser = await userApi.getCurrentUser();
                setCurrentUserEmail(currentUser.email?.toLowerCase() || '');

                // Ownership: use navigation state when coming from Dashboard table, otherwise fetch
                const state = location.state as LocationState | undefined;
                if (typeof state?.fromDashboard === 'boolean') {
                    const isMyCustomer = !state.fromDashboard;
                    setIsUserCustomer(isMyCustomer);
                    if (!isMyCustomer) {
                        setDashboardEnabled(true);
                    }
                } else {
                    const myCustomers = await customerApi.getMyCustomerList();
                    const isMyCustomer = myCustomers.some(customer => customer.customer_id === customerId);
                    setIsUserCustomer(isMyCustomer);
                    if (!isMyCustomer) {
                        setDashboardEnabled(true);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setPageError('Error fetching Customer Details.')
            }
        };

        if (customerId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    useEffect(() => {
        if (!currentUserEmail || !customerData?.customer_details) {
            return;
        }

        const normalizedCoAuthors = (customerData.customer_details.co_authors || [])
            .map((coAuthor) => (typeof coAuthor === 'string' ? coAuthor : coAuthor.email).toLowerCase());
        if (normalizedCoAuthors.includes(currentUserEmail)) {
            setIsUserCustomer(true);
            // setDashboardEnabled(false);
        }
    }, [currentUserEmail, customerData]);

    // When dashboard view is enabled, fetch dashboard data (refetch on every switch to dashboard so edits are reflected)
    useEffect(() => {
        if (customerId && dashboardEnabled) {
            fetchDashboard(customerId);
        }
    }, [customerId, dashboardEnabled, fetchDashboard]);

    // Preload dashboard-only sections so the long-form dashboard renders with complete data.
    useEffect(() => {
        if (!customerId || !dashboardEnabled) return;

        const dashboardSectionsToFetch: CustomerSection[] = [
            'goals',
            'projects',
            'technology',
            'applications',
            'aop',
            'frameworks',
        ];

        void Promise.allSettled(
            dashboardSectionsToFetch.map((section) => fetchSection(customerId, section, true))
        );
    }, [customerId, dashboardEnabled, fetchSection]);

    // Lazy-load section data when user switches to a tab (edit mode only; dashboard mode uses fetchDashboard)
    useEffect(() => {
        if (!customerId || dashboardEnabled) return;
        const activeTabId = activeTab;

        // Technology tab: always refetch on switch so tab shows fresh data from API
        if (activeTabId === 'technology') {
            fetchSection(customerId, 'technology', true);
        }

        const sectionsToFetch: CustomerSection[] = [];
        switch (activeTabId) {
            case 'company-details':
                break;
            case 'goals':
                sectionsToFetch.push('goals');
                break;
            case 'technology':
                break;
            case 'applications':
                sectionsToFetch.push('applications');
                break;
            case 'art-of-the-possible':
                sectionsToFetch.push('aop');
                break;
            case 'transformation':
                sectionsToFetch.push('maturity');
                break;
            case 'frameworks':
                sectionsToFetch.push('frameworks');
                break;
            case 'projects':
                sectionsToFetch.push('projects');
                break;
            default:
                break;
        }

        sectionsToFetch.forEach((section) => {
            if (!isSectionLoaded(section)) {
                fetchSection(customerId, section);
            }
        });
    }, [customerId, dashboardEnabled, activeTab, fetchSection, isSectionLoaded]);

    // Function to handle tab click
    const handleTabClick = async (tabId: string) => {
        // All tabs now use context data directly, no need to refetch
        setActiveTab(tabId);
    };

    const updateActiveDashboardSection = useCallback(() => {
        if (!dashboardEnabled) return;

        const container = mainContentRef.current;
        if (!container) return;

        const containerTop = container.getBoundingClientRect().top;
        const activationOffset = 140;
        let currentSection: DashboardSectionId = 'customer-dashboard';

        DASHBOARD_SECTIONS.forEach(({ id }) => {
            const element = container.querySelector<HTMLElement>(`#${getDashboardSectionAnchorId(id)}`);
            if (!element) return;

            const relativeTop = element.getBoundingClientRect().top - containerTop;
            if (relativeTop <= activationOffset) {
                currentSection = id;
            }
        });

        setDashActiveTab((prev) => (prev === currentSection ? prev : currentSection));
    }, [dashboardEnabled]);

    const scrollToDashboardSection = useCallback((sectionId: DashboardSectionId) => {
        const container = mainContentRef.current;
        const target = container?.querySelector<HTMLElement>(`#${getDashboardSectionAnchorId(sectionId)}`);
        if (!container || !target) return;

        target.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);

    useEffect(() => {
        if (!dashboardEnabled) return;

        const container = mainContentRef.current;
        if (!container) return;

        updateActiveDashboardSection();
        container.addEventListener('scroll', updateActiveDashboardSection, { passive: true });
        window.addEventListener('resize', updateActiveDashboardSection);

        return () => {
            container.removeEventListener('scroll', updateActiveDashboardSection);
            window.removeEventListener('resize', updateActiveDashboardSection);
        };
    }, [dashboardEnabled, updateActiveDashboardSection]);

    const handleDashboardTabClick = async (tabId: string) => {
        const sectionId = tabId as DashboardSectionId;
        setDashActiveTab(sectionId);
        scrollToDashboardSection(sectionId);
    };

    const handleDashboardToggle = () => {
        // Only allow owners to toggle between dashboard and edit modes
        if (!isUserCustomer) {
            return; // Non-owners cannot switch to edit mode
        }
        
        // Context already has the latest data, no need to refetch
        setDashboardEnabled(dashboardEnabled ? false : true)
    }

    const handleNewProjectClick = () => {
        // Only owners can create new projects
        if (!isUserCustomer) {
            return;
        }
        
        // Switch to edit mode and projects tab
        setDashboardEnabled(false);
        setActiveTab('projects');
        setShouldOpenNewProject(true);
    }

    const handleFormOpened = () => {
        setShouldOpenNewProject(false);
    }

    const handleNavigateToGoals = () => {
        setDashActiveTab('key_goals');
        scrollToDashboardSection('key_goals');
    }

    const handleShowHelp = () => {
        setShowHelpModal(true);
    };

    const handleCloseHelp = () => {
        setShowHelpModal(false);
    };

    const handleCustomerDemoClick = () => {
        setShowCustomerDemo(true);
    };

    const handleCloseCustomerDemo = () => {
        setShowCustomerDemo(false);
    };

    const displayError = pageError || contextError;
    const isWaitingForCustomerProfile = Boolean(customerId) && !displayError && !customerData?.customer_details;
    const isPageLoading = Boolean(isLoading || loadingSection || dashboardLoading || isWaitingForCustomerProfile);

    return (
        <div className="relative min-h-screen bg-white font-inter">
            {/* <ApplicationBackground /> */}

            {/* Foreground Content (Header & More) */}
            <div className="flex h-screen flex-col font-inter overflow-hidden">
                <CustomerSidebar
                    isDashboardView={dashboardEnabled}
                    activeTab={dashboardEnabled ? dashActiveTab : activeTab}
                    onTabClick={dashboardEnabled ? handleDashboardTabClick : handleTabClick}
                    onBackClick={handleDashboardToggle}
                    onCustomerDemoClick={handleCustomerDemoClick}
                    isCustomerDemoOpen={showCustomerDemo}
                    onNewProjectClick={handleNewProjectClick}
                    isOwner={isUserCustomer}
                    isLoading={isPageLoading}
                />

                <main ref={mainContentRef} className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-6">
                    {isPageLoading ? (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
                        </div>
                    ) : displayError && !customerData?.customer_details ? (
                        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
                            <h2 className="text-lg font-semibold text-[#0F2A4F]">Unable to load customer profile</h2>
                            <p className="mt-2 text-sm text-gray-600">{displayError}</p>
                        </div>
                    ) : dashboardEnabled ? (
                        <>
                            <CustomerDashboardTab customerData={customerData} isLoading={isLoading || dashboardLoading} error={displayError} onNavigateToGoals={handleNavigateToGoals} />
                        </>
                    ) : (
                        <>
                            {activeTab === 'company-details' && <CompanyDetails onShowHelp={handleShowHelp} />}
                            {activeTab === 'goals' && <GoalsEditTab customerId={customerId} onShowHelp={handleShowHelp} />}
                            {activeTab === 'projects' && <Projects shouldOpenNewProject={shouldOpenNewProject} onFormOpened={handleFormOpened} onShowHelp={handleShowHelp} />}
                            {activeTab === 'technology' && <Technology onShowHelp={handleShowHelp} />}
                            {activeTab === 'applications' && <Applications onShowHelp={handleShowHelp} />}
                            {activeTab === 'art-of-the-possible' && <ArtOfPossible onShowHelp={handleShowHelp} />}
                            {activeTab === 'transformation' && <Transformation />}
                            {activeTab === 'frameworks' && <Frameworks dashboard={false} />}
                        </>
                    )}
                </main>
            </div>

            {/* Help Modal */}
            <HelpModal 
                isOpen={showHelpModal} 
                onClose={handleCloseHelp} 
                currentTab={dashboardEnabled ? dashActiveTab : activeTab} 
            />
            <CustomerDemoPresentation
                customerData={customerData}
                isOpen={showCustomerDemo}
                onClose={handleCloseCustomerDemo}
            />
        </div>
    );
};
